import React, { useMemo } from "react";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { Responsive, WidthProvider } from "react-grid-layout";
import UnifyMFEWidgetRenderer from "../mfe/UnifyMFEWidgetRenderer";
import { generateLayout } from "../../utils/genericCommon";
import css from "../genericMagneticStyle.less";
import FeatureDisabled from "../authorization/FeatureDisabled";
import OverlayNotProvisioned from "../../common/OverlayNotProvisioned";
import { getCookie } from "../../serviceWorker";

const [HbrCard] = reactWrapper(["hbr-card"]);
const ResponsiveGridLayout = WidthProvider(Responsive);

const GridContainer = ({
  filterDashlets = [],
  dashlets = [],
  checkPageAccess = true,
  accessData = { disabled: false },
  featureDisabledComponent: FeatureDisabledComponent = FeatureDisabled,
  componentId = "",
  routeGuard = null,
  mfeErrorComponent = undefined,
  mfeRenderError = undefined,
}) => {
  const RouteGuard = routeGuard;
  const isUIconvergence = Boolean(getCookie("cl-analytics"));
  const marginPix = 16;
  const containerRowHeightPix = 10;
  const gridProps = {
    rowHeight: containerRowHeightPix,
    isDraggable: false,
    isResizable: false,
    margin: [0, 0], // BUGGY
    zIndex: 8888,
    breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
    cols: { lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 },
    useCSSTransforms: false,
  };

  const widgetGridProps = {
    rowHeight: 0,
    margin: [marginPix, marginPix], //height united is somehow caluclated from 0 + 16, line 84
    isDraggable: false,
    isResizable: false,
    breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
    cols: { lg: 12, md: 12, sm: 12, xs: 4, xxs: 4 }, //    cols: {lg:12, md: 12, sm: 6, xs: 4, xxs: 2},
    containerPadding: [24, 24],
    useCSSTransforms: false,
  };

  //When in responsive mode, you should supply at least one breakpoint via the layouts property.
  //When using layouts, it is best to supply as many breakpoints as possible, especially the largest one. If the largest is provided, RGL will attempt to interpolate the rest.
  const layouts = { lg: generateLayout() };

  const topDashlets = useMemo(() => {
    return accessData.disabled === true
      ? filterDashlets.filter((item) => item.checkAccess !== true)
      : filterDashlets;
  }, []);

  const contentDashlets = useMemo(() => {
    return accessData.disabled === true
      ? checkPageAccess === true
        ? []
        : dashlets.filter((item) => item.checkAccess !== true)
      : dashlets;
  }, []);

  const shouldShowOverlayNotProvisioned =
    isUIconvergence === true && accessData.overlayNotProvisioned === true;

  return (
    <div className={"converged-grid-container"}>
      {RouteGuard && <RouteGuard />}
      {topDashlets.length > 0 && (
        <div
          key="filters"
          className={css["filter-widget-area"]}
          // bug: https://github.com/STRML/react-grid-layout/issues/493
        >
          <ResponsiveGridLayout
            containerPadding={[24, 24]}
            {...gridProps}
            layouts={layouts}
          >
            {topDashlets.map((dashlet) => (
              <div
                key={dashlet.name}
                data-grid={dashlet.layout}
                style={dashlet.style}
              >
                <dashlet.component />
              </div>
            ))}
          </ResponsiveGridLayout>
        </div>
      )}
      {shouldShowOverlayNotProvisioned ? (
        <div className="width-full va-mfe-page-container">
          <OverlayNotProvisioned />
        </div>
      ) : componentId ? (
        <div className="width-full va-mfe-page-container">
          <UnifyMFEWidgetRenderer
            isCard
            componentId={componentId}
            errorComponent={mfeErrorComponent}
            renderError={mfeRenderError}
          />
        </div>
      ) : (
        <div key="widgets" className={css["route-frame-widget-area"]}>
          <ResponsiveGridLayout {...widgetGridProps} layouts={layouts}>
            {accessData.disabled === true && checkPageAccess === true ? (
              <div key="default" data-grid={{ x: 0, y: 0, w: 12, h: 20 }}>
                <HbrCard
                  className="widget-container"
                  data-testid="page_access_disabled"
                  container
                >
                  <FeatureDisabledComponent />
                </HbrCard>
              </div>
            ) : (
              contentDashlets.map((item) => (
                <div
                  key={item.name}
                  data-grid={{ ...item.layout, h: item.layout.h + 1 }} // for margin bug
                  style={item.style}
                >
                  <item.component />
                </div>
              ))
            )}
          </ResponsiveGridLayout>
        </div>
      )}
    </div>
  );
};

GridContainer.propTypes = {
  filterDashlets: PropTypes.array,
  dashlets: PropTypes.array,
  checkPageAccess: PropTypes.bool,
  accessData: PropTypes.object,
  featureDisabledComponent: PropTypes.func,
  componentId: PropTypes.string,
  routeGuard: PropTypes.elementType,
  mfeErrorComponent: PropTypes.node,
  mfeRenderError: PropTypes.func,
};

export default GridContainer;
